import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Sparkles,
  Heart,
  Bot,
  Cog,
  Sun,
  CheckCircle2,
} from "lucide-react";
import SEO from "../components/SEO";
import HeroSlideshow from "../components/HeroSlideshow";
import FeatureStrip from "../components/FeatureStrip";
import { DoodleRocket, DoodleGear, DoodleBulb, DoodlePlus } from "../components/Doodles";
import ReviewsSection from "../components/ReviewsSection";
import InquiryForm from "../components/InquiryForm";
import { fetchHomepageContent, fetchSitePrograms } from "../lib/api";

// Static fallback content used until the homepage CMS singleton loads.
const FALLBACK_HOW = [
  { title: "Choose a program", description: "Browse hands-on STEM, robotics, camps, or play-based support." },
  { title: "Book a trial or send an inquiry", description: "Tell us your child's age and goals. We reply within one business day." },
  { title: "We match your child to the right class", description: "Our coordinator confirms the perfect fit and gets you started." },
];
const FALLBACK_WHY = [
  "Hands-on learning, never just worksheets",
  "Small-group classes with attentive coaches",
  "Programs for different learning styles and ages",
  "STEM, camps, and child development support under one trusted roof",
];

// Curated 4 home cards — match the requested taxonomy. We pull live data from
// /api/site/programs but pin display order / copy here so the homepage stays
// parent-friendly even if a program is renamed in CMS.
const HOME_CARDS = [
  {
    slug: "stem-engineering",
    title: "STEM Engineering",
    summary:
      "Hands-on engineering projects using brick-based building, motors, gears, and mechanical models.",
    icon: Cog,
    photo: "https://images.unsplash.com/photo-1518792528501-352f829886dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxjaGlsZHJlbiUyMHBsYXlpbmclMjBlbmdpbmVlcmluZyUyMHRveXMlMjBtb2RlbHxlbnwwfHx8fDE3OTIzMDY2MTN8MA&ixlib=rb-4.1.0&q=85&w=800",
    alt: "Child happily building a motorized engineering model with gears",
    accent: "#6DBE45",
  },
  {
    slug: "robotics-coding",
    title: "Robotics & Coding",
    summary:
      "Robotics and logic-based challenges that help children build problem-solving and technical confidence.",
    icon: Bot,
    photo: "https://images.pexels.com/photos/7743255/pexels-photo-7743255.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=800",
    alt: "Kids exploring robotics and coding on an interactive screen",
    accent: "#123B6D",
  },
  {
    slug: "summer-camps",
    title: "Camps",
    href: "/camps",
    summary:
      "School-break camps with hands-on STEM, building challenges, robotics, and creative projects.",
    icon: Sun,
    photo: "https://images.unsplash.com/photo-1589737172794-8bc7dab0ac22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxjaGlsZHJlbiUyMHBsYXlpbmclMjBlbmdpbmVlcmluZyUyMHRveXMlMjBtb2RlbHxlbnwwfHx8fDE3OTIzMDY2MTN8MA&ixlib=rb-4.1.0&q=85&w=800",
    alt: "Children enjoying a hands-on building workshop at camp",
    accent: "#FF8C42",
  },
  {
    slug: "play-based-support",
    title: "Play-Based Behaviour Support",
    href: "/play-based-support",
    summary:
      "1-on-1 support for emotional regulation, focus, confidence, routines, and social skills.",
    icon: Heart,
    photo: "https://images.pexels.com/photos/8923799/pexels-photo-8923799.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=800",
    alt: "Warm one-on-one play-based learning session with a coach",
    accent: "#00A6B2",
  },
];

export default function HomePage() {
  const [homepage, setHomepage] = useState(null);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    fetchHomepageContent().then(setHomepage).catch(() => {});
    fetchSitePrograms().then(setPrograms).catch(() => {});
  }, []);

  const programBySlug = (slug) => programs.find((p) => p.slug === slug);

  const how = homepage?.how_it_works_items?.length
    ? homepage.how_it_works_items
    : FALLBACK_HOW;
  const why = homepage?.why_choose_items?.length
    ? homepage.why_choose_items
    : FALLBACK_WHY;

  return (
    <>
      <SEO
        path="/"
        title="Nexora Learning & Development Centre | STEM, Robotics, Camps & Play-Based Support in Richmond BC"
        description="Hands-on STEM engineering, robotics & coding, school-break camps, and play-based behaviour support for children in Richmond BC. Book a trial class today."
      />

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: "#FFFCF7" }}
        data-testid="home-hero"
      >
        <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#EAF7FB] opacity-70 pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-[24rem] h-[24rem] rounded-full bg-[#FFF1E8] opacity-70 pointer-events-none" />
        <DoodleRocket className="hidden lg:block w-14 top-16 left-[54%] opacity-60" color="#123B6D" />
        <DoodleBulb className="hidden md:block w-10 top-28 right-10 opacity-55" />
        <DoodleGear className="hidden md:block w-12 bottom-14 left-8 opacity-45" />
        <DoodlePlus className="hidden lg:block w-4 top-40 left-[48%] opacity-60" color="#FF8C42" />
        <DoodlePlus className="hidden lg:block w-3 bottom-24 right-[30%] opacity-60" color="#8B5CF6" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 pb-14 lg:pb-20 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="pill bg-white border border-slate-200 text-[#123B6D] shadow-sm">
              <Sparkles className="h-3 w-3 text-[#FF8C42]" />
              Nexora Learning & Development Centre · Richmond BC
            </span>
            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-extrabold mt-5 max-w-4xl leading-[1.04]"
              style={{ color: "#123B6D" }}
              data-testid="home-hero-headline"
            >
              Helping Kids Build Skills, Confidence & Creativity.
            </h1>
            <span className="mt-4 flex h-1.5 w-44 overflow-hidden rounded-full" aria-hidden="true" data-testid="hero-color-bar">
              <span className="flex-1" style={{ backgroundColor: "#6DBE45" }} />
              <span className="flex-1" style={{ backgroundColor: "#00A6B2" }} />
              <span className="flex-1" style={{ backgroundColor: "#FF8C42" }} />
              <span className="flex-1" style={{ backgroundColor: "#FACC15" }} />
            </span>
            <p
              className="mt-5 max-w-2xl text-base sm:text-lg text-slate-700 leading-relaxed"
              data-testid="home-hero-subhead"
            >
              Nexora offers hands-on STEM engineering, robotics & coding,
              school-break camps, and play-based behaviour support for children in
              Richmond BC.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/trial-class"
                data-testid="home-hero-primary-cta"
                className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-white shadow-sm transition-opacity hover:opacity-90"
                style={{ backgroundColor: "#FF8C42" }}
              >
                Book a trial class
                <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a
                href="#inquire"
                data-testid="home-hero-secondary-cta"
                className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold border-2 transition-colors hover:bg-white/60"
                style={{ borderColor: "#00A6B2", color: "#00A6B2" }}
              >
                Ask about a program
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <HeroSlideshow />
          </div>
        </div>
      </section>

      {/* FEATURE BENEFITS STRIP */}
      <FeatureStrip />

      {/* QUICK CTA STRIP */}
      <section
        className="border-y border-slate-200/70"
        style={{ backgroundColor: "#EAF7FB" }}
        data-testid="home-quick-cta"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-7">
            <p className="font-display text-xl sm:text-2xl font-extrabold leading-snug" style={{ color: "#123B6D" }}>
              Not sure which program is right for your child?
            </p>
            <p className="mt-1 text-sm text-slate-600">
              Our coordinators will help you pick the right fit — and book a trial so your child can try it first.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-wrap gap-2.5 lg:justify-end">
            <Link
              to="/trial-class"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold text-white"
              style={{ backgroundColor: "#FF8C42" }}
              data-testid="home-quick-trial-cta"
            >
              Book a trial class
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            <a
              href="#inquire"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold border-2"
              style={{ borderColor: "#00A6B2", color: "#00A6B2" }}
              data-testid="home-quick-info-cta"
            >
              Request information
            </a>
            <Link
              to="/programs"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold border-2"
              style={{ borderColor: "#123B6D", color: "#123B6D" }}
              data-testid="home-quick-programs-cta"
            >
              View programs
            </Link>
          </div>
        </div>
      </section>

      {/* PROGRAM CATEGORY CARDS (4) */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20"
        data-testid="home-program-cards"
      >
        <div className="max-w-2xl">
          <p className="eyebrow" style={{ color: "#00A6B2" }}>What Nexora offers</p>
          <h2
            className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-extrabold mt-3 leading-[1.08]"
            style={{ color: "#123B6D" }}
          >
            Four ways we support your child.
          </h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {HOME_CARDS.map((c) => {
            const p = programBySlug(c.slug) || {};
            const accent = c.accent || p.accent_color || "#0369A1";
            const Icon = c.icon;
            const href = c.href || `/programs/${c.slug}`;
            return (
              <Link
                key={c.slug}
                to={href}
                data-testid={`home-program-card-${c.slug}`}
                className="group flex flex-col rounded-[1.75rem] overflow-hidden bg-white border border-slate-200/70 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300 transition-all duration-300"
              >
                <div className="relative h-36 overflow-hidden">
                  <img
                    src={c.photo}
                    alt={c.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute inset-x-0 top-0 h-1.5" style={{ backgroundColor: accent }} />
                  <span
                    className="absolute -bottom-5 left-5 flex h-11 w-11 items-center justify-center rounded-2xl shadow-md border border-white"
                    style={{ backgroundColor: "#FFFFFF", color: accent }}
                  >
                    <Icon className="h-5.5 w-5.5" style={{ height: 22, width: 22 }} />
                  </span>
                </div>
                <div className="flex flex-col flex-1 p-6 pt-8">
                  <h3 className="font-display text-lg font-extrabold tracking-tight leading-snug" style={{ color: "#123B6D" }}>
                    {c.title}
                  </h3>
                  {p.age_range && (
                    <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mt-1">
                      {p.age_range}
                    </p>
                  )}
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-4 flex-1">
                    {c.summary}
                  </p>
                  <span
                    className="mt-4 inline-flex items-center gap-1 text-xs font-bold transition-transform group-hover:translate-x-0.5"
                    style={{ color: accent }}
                  >
                    Learn more
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* WHY PARENTS CHOOSE NEXORA */}
      <section
        className="border-y border-slate-200/70"
        style={{ backgroundColor: "#F5F7FA" }}
        data-testid="home-why-choose"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow" style={{ color: "#00A6B2" }}>Why parents choose Nexora</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-3 leading-[1.08]" style={{ color: "#123B6D" }}>
              An education centre parents actually recommend.
            </h2>
          </div>
          <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {why.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 rounded-2xl bg-white border border-slate-200 p-4"
              >
                <span
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: "#E6F7F8", color: "#00A6B2" }}
                >
                  <CheckCircle2 className="h-4 w-4" />
                </span>
                <span className="text-sm text-slate-700 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20" data-testid="home-how-it-works">
        <div className="max-w-2xl">
          <p className="eyebrow" style={{ color: "#FF8C42" }}>How it works</p>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-3 leading-[1.08]" style={{ color: "#123B6D" }}>
            Getting started takes 3 simple steps.
          </h2>
        </div>
        <ol className="mt-10 grid md:grid-cols-3 gap-5">
          {how.map((step, i) => (
            <li
              key={step.title || i}
              className="rounded-[1.5rem] bg-white border border-slate-200 p-6"
              data-testid={`home-how-step-${i}`}
            >
              <span
                className="flex h-10 w-10 items-center justify-center rounded-full text-white font-display font-extrabold"
                style={{ backgroundColor: "#FF8C42" }}
              >
                {i + 1}
              </span>
              <h3 className="mt-4 font-display text-lg font-extrabold" style={{ color: "#123B6D" }}>
                {step.title}
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                {step.description}
              </p>
            </li>
          ))}
        </ol>
      </section>

      {/* REVIEWS */}
      <ReviewsSection />

      {/* FINAL CTA + INQUIRY */}
      <section
        id="inquire"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24"
        data-testid="home-inquire"
      >
        <div className="rounded-[2.5rem] grid lg:grid-cols-12 gap-10 p-8 sm:p-12" style={{ backgroundColor: "#123B6D", color: "#FFFFFF" }}>
          <div className="lg:col-span-5">
            <p className="eyebrow" style={{ color: "#EAF7FB" }}>Talk to us</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.08]">
              Ready when you are.
            </h2>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: "#C7D6E9" }}>
              Tell us your child's age and what you're hoping to find. A Nexora coordinator
              replies within one business day.
            </p>
            <Link
              to="/trial-class"
              className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold"
              style={{ backgroundColor: "#FF8C42", color: "#FFFFFF" }}
              data-testid="home-final-trial-cta"
            >
              Book a trial class
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 rounded-[1.75rem] bg-white p-7" style={{ color: "#0F172A" }}>
            <InquiryForm
              source="home_inquiry"
              sourcePage="/"
              title="Quick inquiry"
              subtitle="Required fields are marked with *"
              ctaLabel="Send inquiry"
            />
          </div>
        </div>
      </section>
    </>
  );
}
